import React, { useState } from 'react';
import {
  AlertCircle,
  CheckCircle,
  CheckCircle2,
  Clock,
  MessageSquare,
  Send,
  XCircle,
} from 'lucide-react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import {
  Proposal,
  RespondToProposalRequest,
  useCreateProposalMutation,
  useGetProposalsByUserQuery,
  useGetProposalsRequiringResponseQuery,
  useGetProposalSummaryQuery,
  useRespondToProposalMutation,
} from '@/apis/extended/proposalApi';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

interface ProposalManagerProps {
  contractId: string;
  userRole: 'buyer' | 'seller' | 'agent';
  onProposalCreated?: (proposal: Proposal) => void;
  onProposalResponded?: (proposal: Proposal) => void;
}

export const ProposalManager: React.FC<ProposalManagerProps> = ({
  contractId,
  userRole,
  onProposalCreated,
  onProposalResponded,
}) => {
  const [selectedProposal, setSelectedProposal] = useState<Proposal | null>(
    null
  );
  const [responseMessage, setResponseMessage] = useState('');
  const [showResponseForm, setShowResponseForm] = useState(false);

  // API hooks
  const [createProposal, { isLoading: isCreating }] =
    useCreateProposalMutation();
  const [respondToProposal, { isLoading: isResponding }] =
    useRespondToProposalMutation();

  const {
    data: proposalsRequiringResponse,
    refetch: refetchRequiringResponse,
  } = useGetProposalsRequiringResponseQuery(
    { contractId },
    { skip: !contractId }
  );

  const { data: proposalsByUser, refetch: refetchByUser } =
    useGetProposalsByUserQuery({ contractId }, { skip: !contractId });

  const { data: proposalSummary, refetch: refetchSummary } =
    useGetProposalSummaryQuery({ contractId }, { skip: !contractId });

  const handleCreateProposal = async (
    clauseId: string,
    clauseHierarchicalId: string,
    originalValue: any,
    proposedValue: any,
    message?: string
  ) => {
    try {
      const result = await createProposal({
        contractId,
        clauseId,
        clauseHierarchicalId,
        originalValue,
        proposedValue,
        message,
      }).unwrap();

      toast.success('Proposal created successfully');
      onProposalCreated?.(result.proposal);

      // Refetch data
      refetchRequiringResponse();
      refetchByUser();
      refetchSummary();
    } catch (error) {
      console.error('Error creating proposal:', error);
      toast.error('Failed to create proposal');
    }
  };

  const handleRespondToProposal = async (
    proposalId: string,
    status: 'accepted' | 'rejected' | 'countered'
  ) => {
    let result: Awaited<
      ReturnType<ReturnType<typeof respondToProposal>['unwrap']>
    >;
    try {
      const responseData: RespondToProposalRequest = {
        status,
        message: responseMessage || undefined,
      };

      result = await respondToProposal({
        proposalId,
        data: responseData,
      }).unwrap();
    } catch (error) {
      console.error('Error responding to proposal:', error);
      toast.error('Failed to respond to proposal');
      return;
    }

    // The response saved. Anything below failing (a refetch of a query that
    // never started, a parent callback) must not also report a failure.
    toast.success(`Proposal ${status} successfully`);
    setSelectedProposal(null);
    setResponseMessage('');
    setShowResponseForm(false);
    try {
      onProposalResponded?.(result.proposal);
      refetchRequiringResponse();
      refetchByUser();
      refetchSummary();
    } catch (error) {
      console.error('Refresh after responding to proposal failed:', error);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'accepted':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'rejected':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'countered':
        return <MessageSquare className="h-4 w-4 text-blue-500" />;
      case 'expired':
        return <Clock className="h-4 w-4 text-gray-500" />;
      default:
        return <Clock className="h-4 w-4 text-yellow-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'accepted':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'countered':
        return 'bg-blue-100 text-blue-800';
      case 'expired':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const proposalsRequiringResponseList =
    proposalsRequiringResponse?.proposals || [];
  const proposalsByUserList = proposalsByUser?.proposals || [];
  const summary = proposalSummary?.summary;

  return (
    <div className="space-y-6">
      {/* Proposal Summary */}
      {summary && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Proposal Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">
                  {summary.total}
                </div>
                <div className="text-sm text-gray-500">Total</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-600">
                  {summary.pending}
                </div>
                <div className="text-sm text-gray-500">Pending</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {summary.accepted}
                </div>
                <div className="text-sm text-gray-500">Accepted</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">
                  {summary.rejected}
                </div>
                <div className="text-sm text-gray-500">Rejected</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {summary.countered}
                </div>
                <div className="text-sm text-gray-500">Countered</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Proposals Requiring Response */}
      {proposalsRequiringResponseList.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-orange-500" />
              Proposals Requiring Your Response
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {proposalsRequiringResponseList.map((proposal) => (
                <div key={proposal._id} className="border rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline">{proposal.clauseId}</Badge>
                        <Badge className={getStatusColor(proposal.status)}>
                          {proposal.status}
                        </Badge>
                      </div>
                      <div className="text-sm text-gray-600 mb-2">
                        <strong>Original:</strong>{' '}
                        {typeof proposal.originalValue === 'object'
                          ? JSON.stringify(proposal.originalValue, null, 2)
                          : String(proposal.originalValue ?? '—')}
                      </div>
                      <div className="text-sm text-gray-600 mb-2">
                        <strong>Proposed:</strong>{' '}
                        {typeof proposal.proposedValue === 'object'
                          ? JSON.stringify(proposal.proposedValue, null, 2)
                          : String(proposal.proposedValue ?? '—')}
                      </div>
                      {proposal.message && (
                        <div className="text-sm text-gray-600 mb-2">
                          <strong>Message:</strong> {proposal.message}
                        </div>
                      )}
                      <div className="text-xs text-gray-500">
                        Proposed by{' '}
                        {proposal.proposedByActor ?? proposal.proposedBy} •{' '}
                        {new Date(proposal.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedProposal(proposal);
                          setShowResponseForm(true);
                        }}
                      >
                        Respond
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Your Proposals */}
      {proposalsByUserList.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Your Proposals
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {proposalsByUserList.map((proposal) => (
                <div key={proposal._id} className="border rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline">{proposal.clauseId}</Badge>
                        <Badge className={getStatusColor(proposal.status)}>
                          {proposal.status}
                        </Badge>
                      </div>
                      <div className="text-sm text-gray-600 mb-2">
                        <strong>Original:</strong>{' '}
                        {typeof proposal.originalValue === 'object'
                          ? JSON.stringify(proposal.originalValue, null, 2)
                          : String(proposal.originalValue ?? '—')}
                      </div>
                      <div className="text-sm text-gray-600 mb-2">
                        <strong>Proposed:</strong>{' '}
                        {typeof proposal.proposedValue === 'object'
                          ? JSON.stringify(proposal.proposedValue, null, 2)
                          : String(proposal.proposedValue ?? '—')}
                      </div>
                      {proposal.message && (
                        <div className="text-sm text-gray-600 mb-2">
                          <strong>Message:</strong> {proposal.message}
                        </div>
                      )}
                      {proposal.response && (
                        <div className="text-sm text-gray-600 mb-2">
                          <strong>Response:</strong> {proposal.response.message}
                          <span className="text-xs text-gray-500 ml-2">
                            by {proposal.response.respondedBy} •{' '}
                            {new Date(
                              proposal.response.respondedAt!
                            ).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                      <div className="text-xs text-gray-500">
                        Proposed by{' '}
                        {proposal.proposedByActor ?? proposal.proposedBy} •{' '}
                        {new Date(proposal.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(proposal.status)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Response Form Modal */}
      {showResponseForm && selectedProposal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle>Respond to Proposal</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Clause: {selectedProposal.clauseId}</Label>
                <div className="text-sm text-gray-600 mt-1">
                  <strong>Original:</strong>{' '}
                  {typeof selectedProposal.originalValue === 'object'
                    ? JSON.stringify(selectedProposal.originalValue, null, 2)
                    : String(selectedProposal.originalValue ?? '—')}
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  <strong>Proposed:</strong>{' '}
                  {typeof selectedProposal.proposedValue === 'object'
                    ? JSON.stringify(selectedProposal.proposedValue, null, 2)
                    : String(selectedProposal.proposedValue ?? '—')}
                </div>
              </div>

              <div>
                <Label htmlFor="response-message">
                  Response Message (Optional)
                </Label>
                <Textarea
                  id="response-message"
                  value={responseMessage}
                  onChange={(e) => setResponseMessage(e.target.value)}
                  placeholder="Add a message explaining your response..."
                  rows={3}
                />
              </div>

              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowResponseForm(false);
                    setSelectedProposal(null);
                    setResponseMessage('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() =>
                    handleRespondToProposal(selectedProposal._id, 'rejected')
                  }
                  disabled={isResponding}
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Reject
                </Button>
                <Button
                  onClick={() =>
                    handleRespondToProposal(selectedProposal._id, 'accepted')
                  }
                  disabled={isResponding}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Accept
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};
